export interface CurrencyDef {
  code: string;
  symbol: string;
  name: string;
}

export const CURRENCY_LIST: CurrencyDef[] = [
  { code: 'INR', symbol: '₹',    name: 'Indian Rupee'       },
  { code: 'USD', symbol: '$',    name: 'US Dollar'          },
  { code: 'EUR', symbol: '€',    name: 'Euro'               },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham'         },
  { code: 'GBP', symbol: '£',    name: 'British Pound'      },
  { code: 'HKD', symbol: 'HK$',  name: 'Hong Kong Dollar'   },
  { code: 'CHF', symbol: 'Fr',   name: 'Swiss Franc'        },
  { code: 'SGD', symbol: 'S$',   name: 'Singapore Dollar'   },
  { code: 'JPY', symbol: '¥',    name: 'Japanese Yen'       },
  { code: 'CAD', symbol: 'CA$',  name: 'Canadian Dollar'    },
  { code: 'AUD', symbol: 'A$',   name: 'Australian Dollar'  },
  { code: 'ZAR', symbol: 'R',    name: 'South African Rand' },
  { code: 'QAR', symbol: 'QR',   name: 'Qatari Riyal'       },
  { code: 'BHD', symbol: 'BD',   name: 'Bahraini Dinar'     },
  { code: 'THB', symbol: '฿',    name: 'Thai Baht'          },
  { code: 'MYR', symbol: 'RM',   name: 'Malaysian Ringgit'  },
];

export const CURRENCY_CODES = CURRENCY_LIST.map(c => c.code);

// ── Exchange rates (BusinessSettings.exchangeRates) ───────────────────────
// The Dashboard's "Exchange Rates" card configures up to three currency
// pairs; the same list is meant to feed invoice currency conversion later.
// Look one up by direction, transparently using the inverse of a stored
// pair when only the opposite direction was entered.
export interface ExchangeRatePair {
  base:  string;
  quote: string;
  rate:  number;
}

export function findExchangeRate(
  pairs: ExchangeRatePair[] | undefined,
  base: string,
  quote: string,
): number | null {
  if (!pairs || base === quote) return base === quote ? 1 : null;
  for (const p of pairs) {
    if (!p.rate || p.rate <= 0) continue;
    if (p.base === base && p.quote === quote) return p.rate;
    if (p.base === quote && p.quote === base) return 1 / p.rate;
  }
  return null;
}

export function getCurrencySymbol(code: string): string {
  return CURRENCY_LIST.find(c => c.code === code)?.symbol ?? code;
}

export function getCurrencyName(code: string): string {
  return CURRENCY_LIST.find(c => c.code === code)?.name ?? code;
}

// Full precision formatting — uses Indian number system for INR
export function fmtMoney(amount: number, currencyCode: string): string {
  const sym    = getCurrencySymbol(currencyCode);
  const locale = currencyCode === 'INR' ? 'en-IN' : 'en-US';
  return sym + amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Compact formatting — Lakh/Crore for INR, k/M for others
export function fmtCompact(amount: number, currencyCode: string): string {
  const sym = getCurrencySymbol(currencyCode);
  if (currencyCode === 'INR') {
    if (amount >= 10_000_000) return `${sym}${(amount / 10_000_000).toFixed(2)}Cr`;
    if (amount >= 100_000)    return `${sym}${(amount / 100_000).toFixed(1)}L`;
    if (amount >= 1_000)      return `${sym}${(amount / 1_000).toFixed(1)}K`;
    return `${sym}${Math.round(amount)}`;
  }
  if (amount >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000)     return `${sym}${(amount / 1_000).toFixed(1)}k`;
  return `${sym}${Math.round(amount)}`;
}
